import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import {
  CatDropdown,
  CatInputField,
  CatList,
  CatListItem
} from 'components/blocks-components';
import { Conditional } from '@cat-ecom/pcc-components';
import { BlocksChangeEvent } from 'blocks-nextjs/blocks-web-components/events';
import { COUNTRY_CODE, taxModalIntialValue } from '../constants';
import { setInputValue } from '../utils';
import { TaxModalFormProps, FormValues } from '../types';
import { TaxModalFooter } from '../tax-modal-footer';
import { BrazilTaxForm } from '../brazil-tax-form';

export const TaxModalForm = ({ onClose, taxModalProps }: TaxModalFormProps) => {
  const t = useTranslations();
  const methods = useForm<FormValues>({
    defaultValues: taxModalIntialValue
  });

  const { handleSubmit, control, setValue, watch, getValues } = methods;

  const taxIdOptions = [
    {
      label: t('UR_CAT_COMPANY'),
      value: t('UR_CAT_COMPANY')
    },
    {
      label: t('UR_CAT_INDIVIDUAL'),
      value: t('UR_CAT_INDIVIDUAL')
    }
  ];

  const selectedTaxIdOption = watch('taxIdOption');

  const onSubmit = () => {
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
        <Controller
          name="taxIdOption"
          control={control}
          rules={{
            required: true
          }}
          render={({ field }) => (
            <CatDropdown
              {...field}
              name="taxIdOption"
              label={t('TAX_COLLECTION_DROPDOWN')}
              placeholder={t('TAX_COLLECTION_DROPDOWN_PLACEHOLDER')}
              required
              fieldId="taxIdOption"
              position="bottom-start"
              onBlSelect={(e: BlocksChangeEvent) =>
                setInputValue(e, setValue, 'taxIdOption')
              }
              value={getValues('taxIdOption')}
              className="mb-3"
            >
              <CatList>
                {taxIdOptions.map(({ label, value }) => (
                  <CatListItem value={value} key={value}>
                    {label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          )}
        ></Controller>
        <Conditional
          test={taxModalProps.country === COUNTRY_CODE.brazil}
          fallback={
            <Conditional test={!!getValues('taxIdOption')}>
              <Conditional
                test={selectedTaxIdOption === t('UR_CAT_INDIVIDUAL')}
                fallback={
                  <Controller
                    name="companyTaxId"
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field }) => (
                      <CatInputField
                        {...field}
                        name="companyTaxId"
                        label={t('COMPANY_TAX_ID')}
                        required
                        data-testid="companyTaxId"
                        fieldId="companyTaxId"
                        value={getValues('companyTaxId')}
                        onBlChange={(e: BlocksChangeEvent) =>
                          setInputValue(e, setValue, 'companyTaxId')
                        }
                        className="mb-3"
                      />
                    )}
                  ></Controller>
                }
              >
                <Controller
                  name="individualTaxId"
                  control={control}
                  rules={{
                    required: true
                  }}
                  render={({ field }) => (
                    <CatInputField
                      {...field}
                      name="individualTaxId"
                      label={t('INDIVIDUAL_TAX_ID')}
                      required
                      data-testid="individualTaxId"
                      fieldId="individualTaxId"
                      value={getValues('individualTaxId')}
                      onBlChange={(e: BlocksChangeEvent) =>
                        setInputValue(e, setValue, 'individualTaxId')
                      }
                      className="mb-3"
                    />
                  )}
                ></Controller>
              </Conditional>
            </Conditional>
          }
        >
          <BrazilTaxForm />
        </Conditional>
        <TaxModalFooter logoutUrl={taxModalProps.logoutUrl} />
      </form>
    </FormProvider>
  );
};
